import React, { FC, useState, ChangeEvent } from 'react';
import {
    Alert,
    Box,
    Typography,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormGroup,
    Checkbox,
    Button,
} from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { copyOrderTemplate } from 'apollo/templates/order';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { CONSTANTS } from 'config/constants';
import { Order } from 'models/order';

const { COPY_TYPES } = CONSTANTS;

type CopyOrderDialogProps = {
    open: boolean;
    onDialogClose: () => void;
    orderDbId: string;
    orderId: string;
    onCreateCopyOrder: (order: Order) => void;
};
const CopyOrderDialog: FC<CopyOrderDialogProps> = ({
    open,
    onDialogClose,
    orderDbId,
    orderId,
    onCreateCopyOrder,
}: CopyOrderDialogProps) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const [copyType, setCopyType] = useState('RELATED_ORDER');
    const [orderSection, setOrderSection] = useState({
        patient: true,
        construction: true,
        comments: false,
        files: false,
    });
    const { showBoundary } = useDenthubErrorBoundary();

    const [copyOrder] = useMutation(copyOrderTemplate, {
        onCompleted: ({ copyOrder }) => {
            onDialogClose();
            onCreateCopyOrder(copyOrder);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setCopyType((event.target as HTMLInputElement).value);
    };

    const handleSectionChange = (event: ChangeEvent<HTMLInputElement>) => {
        setOrderSection({
            ...orderSection,
            [event.target.name]: event.target.checked,
        });
    };

    const handleCopyOrder = async () => {
        await copyOrder({
            variables: {
                orderDbId,
                copyType,
                orderSection,
            },
        });
    };

    return (
        <DHDialog
            maxWidth="sm"
            fullWidth
            open={open}
            onClose={onDialogClose}
            title={translate('orders:COPY_ORDER_FOR') + ' ' + orderId}
            actions={
                <>
                    <Button variant="contained" onClick={handleCopyOrder} data-test="copy-order-dialog.copy-order-btn">
                        {translate('orders:COPY_ORDER')}
                    </Button>
                </>
            }
        >
            <Box>
                <Box>
                    <Typography variant="h4">{translate('orders:COPY_TYPE')}</Typography>
                </Box>
                <FormControl component="fieldset">
                    <RadioGroup value={copyType} onChange={handleChange}>
                        {COPY_TYPES.map((elem: string) => (
                            <FormControlLabel
                                key={elem}
                                value={elem}
                                control={<Radio color="secondary" data-test={`copy-order-dialog.copy-type.${elem}`} />}
                                label={translate(`orders:${elem}`)}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            </Box>
            <Box mt={2}>
                <Box mb={1}>
                    <Typography variant="h4">{translate('orders:SECTION_TO_BE_COPIED')}</Typography>
                </Box>
                <Box>
                    <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                        {translate('orders:COPY_ORDER_INFO')}
                    </Alert>
                </Box>
                <Box>
                    <FormControl component="fieldset" variant="standard">
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        disabled
                                        checked={orderSection.patient}
                                        onChange={handleSectionChange}
                                        name="patient"
                                        data-test="copy-order-dialog.section.patient"
                                    />
                                }
                                label={translate('orders:PATIENT')}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        disabled
                                        checked={orderSection.construction}
                                        onChange={handleSectionChange}
                                        name="construction"
                                        data-test="copy-order-dialog.section.construction"
                                    />
                                }
                                label={translate('orders:CONSTRUCTION_DETAILS')}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={orderSection.comments}
                                        onChange={handleSectionChange}
                                        name="comments"
                                        data-test="copy-order-dialog.section.comments"
                                    />
                                }
                                label={translate('orders:COMMENTS')}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={orderSection.files}
                                        onChange={handleSectionChange}
                                        name="files"
                                        data-test="copy-order-dialog.section.files"
                                    />
                                }
                                label={translate('orders:ATTACHED_FILES_AND_IMAGES')}
                            />
                        </FormGroup>
                    </FormControl>
                </Box>
            </Box>
        </DHDialog>
    );
};

export default CopyOrderDialog;
